import { Store, Hotel, Plane, Heart, Trophy, Building2, Dices, UtensilsCrossed } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const IndustriesPage = () => {
  const { t } = useLanguage();

  const industries = [
    {
      key: 'casinos',
      icon: Dices,
      title: t('industries.casinos.title'),
      description: t('industries.casinos.description'),
      useCases: t('industries.casinos.useCases') || [],
      image: '/casino-gaming-floor.webp',
      color: 'orange'
    },
    {
      key: 'retail',
      icon: Store,
      title: t('industries.retail.title'),
      description: t('industries.retail.description'),
      useCases: t('industries.retail.useCases') || [],
      image: '/industry_retail.jpg',
      color: 'sky'
    },
    {
      key: 'hospitality',
      icon: Hotel,
      title: t('industries.hospitality.title'),
      description: t('industries.hospitality.description'),
      useCases: t('industries.hospitality.useCases') || [],
      image: '/industry_hotel.webp',
      color: 'amber'
    },
    {
      key: 'airports',
      icon: Plane,
      title: t('industries.airports.title'),
      description: t('industries.airports.description'),
      useCases: t('industries.airports.useCases') || [],
      image: 'https://images.unsplash.com/photo-1771970777749-4478b9e74d09?w=800&q=80',
      color: 'emerald'
    },
    {
      key: 'healthcare',
      icon: Heart,
      title: t('industries.healthcare.title'),
      description: t('industries.healthcare.description'),
      useCases: t('industries.healthcare.useCases') || [],
      image: 'https://images.unsplash.com/photo-1764885517847-79d62138cc58?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      color: 'rose'
    },
    {
      key: 'stadiums',
      icon: Trophy,
      title: t('industries.stadiums.title'),
      description: t('industries.stadiums.description'),
      useCases: t('industries.stadiums.useCases') || [],
      image: '/industry_stadium.webp',
      color: 'violet'
    },
    {
      key: 'corporate',
      icon: Building2,
      title: t('industries.corporate.title'),
      description: t('industries.corporate.description'),
      useCases: t('industries.corporate.useCases') || [],
      image: '/industry-realestate.jpg',
      color: 'slate'
    },
    {
      key: 'restaurants',
      icon: UtensilsCrossed,
      title: t('industries.restaurants.title'),
      description: t('industries.restaurants.description'),
      useCases: t('industries.restaurants.useCases') || [],
      image: '/restaurant-qsr-industry.png',
      color: 'red'
    }
  ];

  const colorClasses = {
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', dot: 'bg-orange-500' },
    sky: { bg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', dot: 'bg-[#0077FF]' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', dot: 'bg-amber-500' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', dot: 'bg-emerald-500' },
    rose: { bg: 'bg-rose-100', text: 'text-rose-600', dot: 'bg-rose-500' },
    violet: { bg: 'bg-violet-100', text: 'text-violet-600', dot: 'bg-violet-500' },
    slate: { bg: 'bg-[#0077FF]/10', text: 'text-[#0077FF]', dot: 'bg-[#0077FF]' },
    red: { bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-500' }
  };

  return (
    <div className="min-h-screen" data-testid="industries-page">
      {/* Hero */}
      <section className="gradient-hero pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-caption text-[#0077FF] mb-4 block">Industries</span>
            <h1 className="text-h1 text-slate-900 mb-6" data-testid="industries-title">
              {t('industries.hero.title')}
            </h1>
            <p className="text-body-lg">
              {t('industries.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry) => {
              const colors = colorClasses[industry.color];
              return (
                <div
                  key={industry.key}
                  className="card-feature group hover:-translate-y-1"
                  data-testid={`industry-card-${industry.key}`}
                >
                  <div className="relative mb-6 overflow-hidden rounded-xl">
                    <img
                      src={industry.image}
                      alt={industry.title}
                      className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className={`absolute top-4 left-4 w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center`}>
                      <industry.icon className={`w-6 h-6 ${colors.text}`} />
                    </div>
                  </div>
                  <h3 className="text-h4 text-slate-900 mb-3">
                    {industry.title}
                  </h3>
                  <p className="text-body text-sm mb-4">
                    {industry.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {industry.useCases.map((useCase, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#0077FF]/10 rounded-full text-xs text-[#0077FF]"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                        {useCase}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Restaurant / QSR Chains — REMOVED: Duplicate recap section, industry already in grid above */}

      {/* CTA */}
      <section className="section-padding bg-slate-900">
        <div className="container-max text-center">
          <h2 className="text-h2 text-white mb-4">
            Your Industry Not Listed?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Tapelligence adapts to any physical environment. Contact us to discuss how the platform can support your specific operational requirements.
          </p>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
